/**
 * Karte „Heat-Up-Wellen je Liste" (Workshop-Lücke N2, Plan 2026-10-29; Till Q6: 8 Webinare, Schwestermarken eine Zeile).
 * Heatmap-Tabelle Publisher-Liste × Welle über die letzten 8 gelaufenen Webinare mit allen drei Wellen — dieselbe Basis wie
 * der Wellen-Vergleich der Karte darüber. Hauptwert Anmeldungen je 1.000 Kontakte; Farbe = Index gegen den Schnitt
 * DERSELBEN Liste (kein Vergleich zwischen Listen). € je 1.000 erst ab 20 Anfos, Abmeldungen je 1.000 als zweite Zahl.
 * Hat eine Liste in einer Welle nie verschickt, bleibt die Zelle leer („—"), nie 0. Backend: `matrix` in /hebel/wellen.
 */
import { FC, ReactNode } from 'react';
import { Box, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { BefundZeile, Hinweis, Laden, int } from '../../../components/auswertungen/kennzahlTeile';
import { useVizStyle } from '../../../components/auswertungen/vizStyle';
import type { HebelGrauSchwellen, MatrixZeile, WelleKey, WellenAntwort, WellenQuoten, WellenZahlen } from '../../../helpers/hebelData';
import { ATTRIBUTION, TERMS } from '../../../helpers/kpiDefinitions';
import { formatNumber } from '../../../helpers/numberUtils';
import { euroJe1000, festeStellen } from './befund';
import { abmeldungenGrau, anmeldungenGrau, euroZeigen, indexFarbe, matrixBefund, zellSort } from './matrix';
import { tag, Unterzeile } from './teile';

const WELLEN: { key: WelleKey; label: string; kurz: string }[] = [
  { key: 'vortag', label: 'Vortag', kurz: 'Vortag' },
  { key: 'frueh', label: 'Webinartag früh', kurz: 'früh' },
  { key: 'spaet', label: 'Webinartag spät', kurz: 'spät' },
];

type Werte = WellenZahlen & WellenQuoten;

/** Inhalt einer Zelle: Anmeldungen je 1.000 groß, darunter Index, €/Anfos und Abmeldungen. */
const Zelle: FC<{
  w: Werte;
  index?: number | null;
  stellen: number;
  /** Stellen der Abmeldungen je 1.000, je Zeile fest (wie die Wellen-Karte: unter 10 eine Stelle). */
  abmStellen: number;
  g: HebelGrauSchwellen;
  kurz: boolean;
  farbe?: string;
  webinare?: { von: number; soll: number };
}> = ({ w, index, stellen, abmStellen, g, kurz, farbe, webinare }) => {
  const grauAnm = anmeldungenGrau(w, g);
  const grauAbm = abmeldungenGrau(w, g);
  const fest = w.anfos - w.anfos_vorlaeufig;
  const inhalt: ReactNode = (
    // Unsicher (zu wenige Anmeldungen): die ganze Zelle blass, auch Index und Unterzeilen.
    <Box sx={{ lineHeight: 1.25, px: kurz ? 0.25 : 0.75, py: 0.5, borderRadius: '4px', backgroundColor: farbe, textAlign: 'right', opacity: grauAnm ? 0.6 : 1 }}>
      <Box component="span" sx={{ fontWeight: 700, color: grauAnm ? 'text.disabled' : undefined }}>
        {w.anmeldungen_je_1000 === null ? '—' : formatNumber(w.anmeldungen_je_1000, stellen)}
      </Box>
      {index !== undefined && index !== null && (
        <Unterzeile>
          Index {formatNumber(index, 0)}
        </Unterzeile>
      )}
      {!kurz && (
        <>
          <Unterzeile>
            {euroZeigen(w, g) ? (
              `${euroJe1000(w.wert_je_1000)} je 1.000`
            ) : (
              <Box component="span" sx={{ color: 'text.disabled' }} title={`unter ${g.anfos} Anfos — kein Euro je 1.000`}>
                {int(fest)} {fest === 1 ? 'Anfo' : 'Anfos'}
              </Box>
            )}
          </Unterzeile>
          <Unterzeile>
            <Box component="span" sx={{ color: grauAbm ? 'text.disabled' : undefined }} title={grauAbm ?? undefined}>
              {w.abmeldungen_je_1000 === null ? '—' : formatNumber(w.abmeldungen_je_1000, abmStellen)} Abm. je 1.000
            </Box>
          </Unterzeile>
        </>
      )}
      {webinare && webinare.von < webinare.soll && <Unterzeile>in {webinare.von} von {webinare.soll} Webinaren</Unterzeile>}
    </Box>
  );
  return grauAnm ? (
    <Tooltip title={grauAnm} arrow placement="top">
      <Box sx={{ cursor: 'help' }}>{inhalt}</Box>
    </Tooltip>
  ) : (
    <>{inhalt}</>
  );
};

/** D16: Standardspalten je Kartenbreite — am Handy ohne Zeilenschnitt (wie bisher fest verdrahtet), wählbar im Menü. */
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['liste', 'vortag', 'frueh', 'spaet', 'schnitt'],
  mittel: ['liste', 'vortag', 'frueh', 'spaet', 'schnitt'],
  schmal: ['liste', 'vortag', 'frueh', 'spaet'],
};

const MatrixKarte: FC<{ data: WellenAntwort | null; fehler: string | null }> = ({ data, fehler }) => {
  const theme = useTheme();
  const viz = useVizStyle();
  const telefon = useMediaQuery(theme.breakpoints.down('sm'));
  const g = data?.grau_schwellen ?? { anfos: 20, anmeldungen: 50, rechtzeitig: 50, abmeldungen: 20 };
  const m = data?.matrix ?? null;
  const n = m?.webinare.length ?? 0;
  // Feste Stellen je ZEILE: verglichen wird nur innerhalb einer Liste (Schwestermarken ~17 je 1.000, Finanztrends ~1,5).
  const stellen = (r: MatrixZeile): number => festeStellen([...WELLEN.map((w) => r.zellen[w.key]?.anmeldungen_je_1000), r.summe.anmeldungen_je_1000]);
  // Abmeldungen je 1.000 mit den Stellen der Wellen-Karte darüber (dort „3,1“; vorher hier fest 2 Stellen, Konsistenzprüfung 29.10.).
  const abmStellen = (r: MatrixZeile): number =>
    Math.min(1, festeStellen([...WELLEN.map((w) => r.zellen[w.key]?.abmeldungen_je_1000), r.summe.abmeldungen_je_1000]));

  const spalten: Spalte<MatrixZeile>[] = [
    {
      key: 'liste',
      label: TERMS.matrixListe.label,
      tip: TERMS.matrixListe.tip,
      pflicht: true,
      // Feste Reihenfolge der API (Finanztrends, KME, DNV, Schwestermarken, weitere) statt Alphabet.
      sortValue: (r) => m?.zeilen.indexOf(r) ?? 0,
      render: (r) => (
        // lang="de": die Seite steht auf lang="en", ohne das trennt `hyphens` deutsche Wörter nicht und `anywhere` brach
        // bei 390 px mitten im Wort („Finanztre|nds“, Abnahme 29.10.); jetzt mit Trennstrich an der Silbe.
        <Box lang="de" sx={{ lineHeight: 1.3, whiteSpace: 'normal', maxWidth: telefon ? 78 : undefined, overflowWrap: 'anywhere', hyphens: 'auto' }}>
          <Box component="span" sx={{ fontWeight: 600 }}>
            {r.label}
          </Box>
          {!telefon && (
            <Unterzeile>
              {r.clients.join(', ')} · {int(r.summe.kontakte)} Kontakte
              {r.webinare < n ? ` · in ${r.webinare} von ${n} Webinaren` : ''}
            </Unterzeile>
          )}
        </Box>
      ),
    },
    ...WELLEN.map(
      ({ key, label, kurz }): Spalte<MatrixZeile> => ({
        key,
        // Telefon: kurze Köpfe, sonst lief die Tabelle bei 390 px gut 60 px über die Karte.
        label: telefon ? kurz : label,
        tip: `${TERMS.welle.tip} Groß: ${TERMS.wellenAnmeldungen.label} je 1.000 Kontakte. Darunter: ${TERMS.matrixIndex.label}, Erwarteter Wert je 1.000 (ab ${g.anfos} Anfos, sonst die Zahl der Anfos) und Abmeldungen je 1.000.`,
        einheit: telefon ? undefined : 'Anm. je 1.000',
        align: 'right',
        // Unsichere Zellen (zu wenige Anmeldungen) sortieren hinter die sicheren — Regel der Sektion (SortableTable).
        unsicher: (r) => (r.zellen[key] ? anmeldungenGrau(r.zellen[key]!, g) : null),
        sortValue: (r) => zellSort(r.zellen[key]),
        render: (r) => {
          const z = r.zellen[key];
          if (!z) {
            return (
              <Tooltip title="Diese Liste hat in dieser Welle in keinem der Webinare verschickt — leer, nicht 0." arrow placement="top">
                <Box component="span" sx={{ cursor: 'help', color: 'text.secondary' }}>
                  —
                </Box>
              </Tooltip>
            );
          }
          const grau = anmeldungenGrau(z, g) !== null;
          return (
            <Zelle
              w={z}
              index={z.index}
              stellen={stellen(r)}
              abmStellen={abmStellen(r)}
              g={g}
              kurz={telefon}
              farbe={indexFarbe(z.index, grau, viz.series1, viz.series2)}
              webinare={{ von: z.webinare, soll: n }}
            />
          );
        },
      }),
    ),
    {
      key: 'schnitt',
      label: TERMS.matrixZeilenschnitt.label,
      tip: TERMS.matrixZeilenschnitt.tip,
      einheit: 'Anm. je 1.000',
      align: 'right',
      unsicher: (r) => anmeldungenGrau(r.summe, g),
      sortValue: (r) => r.summe.anmeldungen_je_1000,
      render: (r) => <Zelle w={r.summe} stellen={stellen(r)} abmStellen={abmStellen(r)} g={g} kurz={telefon} />,
    },
  ];
  const meta = spaltenMeta(spalten);
  const wahl = useSpaltenwahl('hebelMatrix', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);

  return (
    <ChartCard
      title="Heat-Up-Wellen je Liste"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead={
        <>
          Je Liste und Welle: wie viele Anmeldungen 1.000 Heat-Up-Mails bringen, über die letzten {n || 8} gelaufenen Webinare mit allen
          drei Wellen. Die Farbe vergleicht nur innerhalb einer Zeile — blau = diese Welle zieht bei dieser Liste besser als ihr Schnitt,
          orange = schlechter. Zwischen den Listen nicht vergleichen: sie sind verschieden groß und verschieden aktiv. Leer heißt: diese
          Liste hat in dieser Welle nicht verschickt.
        </>
      }
      terms={[
        { label: TERMS.matrixListe.label, tip: TERMS.matrixListe.tip },
        { label: TERMS.matrixIndex.label, tip: TERMS.matrixIndex.tip },
        { label: TERMS.welle.label, tip: TERMS.welle.tip },
        { label: ATTRIBUTION.ersterKontakt.label, tip: ATTRIBUTION.ersterKontakt.tip },
        { label: TERMS.wellenWertJe1000.label, tip: TERMS.wellenWertJe1000.tip },
      ]}
      source="Webinar-Versände mit Empfängern, Anmeldungen, Abmeldungen und Erst-Kontakt-Anfos je Versand"
      freshnessKey="webinar_send_metrics"
    >
      <Box ref={wahl.messRef}>
      {!data ? (
        <Laden fehler={fehler} />
      ) : !m || m.zeilen.length === 0 ? (
        <Hinweis>Keine gelaufenen Webinare mit allen drei Wellen in den letzten {data.wochen} Wochen.</Hinweis>
      ) : (
        <>
          {(() => {
            const befund = matrixBefund(m.zeilen, g);
            return befund ? <BefundZeile text={befund} /> : null;
          })()}
          <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
            {n} Webinare, {tag(m.webinare[m.webinare.length - 1].start_date.slice(0, 10))}–{tag(m.webinare[0].start_date.slice(0, 10))}
            {n < m.soll_webinare && ` (nur ${n} statt ${m.soll_webinare} mit allen drei Wellen in den letzten ${data.wochen} Wochen)`}:{' '}
            {m.webinare.map((w) => w.public_title ?? w.webinar_id).join(' · ')}
          </Box>
          <SortableTable
            rows={m.zeilen}
            columns={spalten.filter((c) => sichtbar.has(c.key))}
            rowKey={(r) => r.liste}
            initialSort={{ key: 'liste', dir: 'asc' }}
            maxHeight={null}
            festeErsteSpalte
            eng
          />
          <Hinweis>
            Gezählt wie in der Karte darüber: Anmeldungen und Anfos beim ersten Kontakt einer Person, Kontakte = zugestellte Mails (bei
            Versänden an die ganze Liste nur die aktiven Empfänger). Erwarteter Wert = Anfos × mittlerer Erwarteter Wert (bei Bestellung,
            netto) der Anfos ihres Webinars; unter {g.anfos} Anfos steht statt Euro die Zahl der Anfos. Grau: unter {g.anmeldungen}{' '}
            Anmeldungen bzw. {g.abmeldungen} Abmeldungen. Teilnehmer fehlen hier: ob die Teilnahme verlässlich gemessen ist, gilt je
            Webinar, nicht je Liste.
          </Hinweis>
        </>
      )}
      </Box>
    </ChartCard>
  );
};

export default MatrixKarte;
